'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { Button } from '../Clickables/Buttons/Button';
import { Heading } from '../Heading/Heading';
import { IStateInfo } from '../FindUSSpecies/helpers/buildStateDataSpecies';
import { TopStateLinkCard } from '../Cards/TopStateLinkCard/TopStateLinkCard';

const DEFAULT_MAX_VISIBLE_ITEMS = 5;

interface IProps {
  className?: string;
  countryId: string;
  countryName: string;
  id?: string;
  clickEventSourceId?: string;
  headingText?: string;
  speciesGroupSEOId?: string;
  topStates: IStateInfo[];
  nrOfVisibleItems?: number;
  hasShowAllButton?: boolean;
}

export const TopStatesSection = ({
  clickEventSourceId,
  headingText = `Best states in this area`,
  id = 'top-states-section',
  countryId,
  countryName,
  topStates,
  speciesGroupSEOId,
  className,
  nrOfVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
  hasShowAllButton = false,
}: IProps) => {
  const [visibleTopStatesItems, setVisibleTopStatesItems] = useState(nrOfVisibleItems);
  const { currentPage } = useAnalytics();
  const statesCount = topStates.length;
  const hasMoreStates = visibleTopStatesItems < statesCount;
  const showAllStatesButton = hasShowAllButton && visibleTopStatesItems < statesCount;
  const showMoreStatesButton = !hasShowAllButton && hasMoreStates;

  const handleShowAll = () => {
    setVisibleTopStatesItems(statesCount);
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopStatesSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleShowMore = () => {
    if (hasMoreStates) {
      setVisibleTopStatesItems(prev => Math.min(prev + nrOfVisibleItems, statesCount));
    }
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopStatesSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  return (
    <div
      id={id}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mb-4 sm:mb-8" variant="sm" as="h2">
        {headingText}
      </Heading>
      <div className="flex flex-col sm:mb-2">
        {topStates.map(({ State, State_Id }, index) => {
          return (
            <TopStateLinkCard
              rank={index + 1}
              key={`state-${State_Id}`}
              countryId={countryId}
              countryName={countryName}
              stateId={State_Id}
              stateName={State}
              speciesGroupSEOId={speciesGroupSEOId}
              className={`w-full ${index >= visibleTopStatesItems ? 'hidden' : ''}`}
              testId={`top-state-card-${State_Id}`}
              clickEventPosition={index}
              clickEventSourceId={clickEventSourceId}
              clickEventSource="TopStatesSection"
            />
          );
        })}

        {showMoreStatesButton && (
          <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleShowMore}>
            Show more states
          </Button>
        )}

        {showAllStatesButton && (
          <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleShowAll}>
            Show all states
          </Button>
        )}
      </div>
    </div>
  );
};
